import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {AuthModel} from './_models'
import * as authHelper from './AuthHelpers'
import {getUserByToken} from './_requests'
import {API_URL, WithChildren} from '../../../../_metronic/helpers'
import {pushEndpointForSignOut} from '../../notifications/core/pushEndpoint'
import {rememberTabBusiness, serverKeepsBusinessPerTab, setBusinessNotice} from './tabBusiness'
import {ACCOUNT_INCOMPLETE_EVENT} from './accountGateCodes'
import axios from 'axios'

const SESSION_REVALIDATE_AFTER_MS = 5 * 60 * 1000

type LogoutOptions = {
  server?: boolean
}

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: AuthModel | undefined
  setCurrentUser: Dispatch<SetStateAction<AuthModel | undefined>>
  /** Resolves once the server has dropped the session (at most a few seconds): a page that reloads waits for it. */
  logout: (options?: LogoutOptions) => Promise<void>
}

/** How long a sign-out waits for the server before the page goes on anyway. */
const LOGOUT_WAIT_MS = 4000

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => Promise.resolve(),
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<AuthModel | undefined>()
  const isSessionValidationInFlightRef = useRef(false)
  const lastSessionValidatedAtRef = useRef(0)

  const clearLocalAuth = (supportOnly = false) => {
    setAuth(undefined)
    if (supportOnly) authHelper.removeSupportAuth()
    else authHelper.removeAuth()
    setCurrentUser(undefined)
  }

  const saveAuth = (nextAuth: AuthModel | undefined) => {
    setAuth(nextAuth)
    if (nextAuth) {
      authHelper.setAuth(nextAuth)
    } else {
      clearLocalAuth(Boolean(currentUser?.support || auth?.support))
    }
  }

  const logout = (options: LogoutOptions = {}): Promise<void> => {
    const supportSession = currentUser?.support?.session_type === 'support' ||
      auth?.support?.session_type === 'support'
    let signedOut: Promise<unknown> = Promise.resolve()
    if (options.server !== false) {
      const logoutUrl = supportSession
        ? `${API_URL}/support-sessions/logout`
        : `${API_URL}/auth/logout`
      if (supportSession) {
        const supportToken = currentUser?.tk || auth?.tk
        signedOut = fetch(logoutUrl, {
          method: 'POST',
          credentials: 'include',
          keepalive: true,
          headers: supportToken ? {Authorization: `Bearer ${supportToken}`} : undefined,
        }).catch(() => {})
      } else {
        // This browser's web push address: the server stops pushing this user's notifications here.
        const pushEndpoint = pushEndpointForSignOut()
        signedOut = axios.post(logoutUrl, pushEndpoint ? {pushEndpoint} : undefined).catch(() => {})
      }
    }
    clearLocalAuth(supportSession)
    // A reload before the server answers would cancel the request and the session cookie would sign the user back in.
    return Promise.race([
      signedOut.then(() => undefined),
      new Promise<void>((resolve) => setTimeout(resolve, LOGOUT_WAIT_MS)),
    ])
  }

  useEffect(() => {
    const handleUnauthorized = (event: Event) => clearLocalAuth(Boolean(
      (event as CustomEvent<{supportSession?: boolean}>).detail?.supportSession
    ))
    window.addEventListener('megapay:auth:unauthorized', handleUnauthorized)
    return () => window.removeEventListener('megapay:auth:unauthorized', handleUnauthorized)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // A request refused with ACCOUNT_INCOMPLETE or DEVICE_LIMIT: the session is read again, and with its
  // accountCompletion or deviceLimit the panel shows only «تکمیل حساب کاربری» or «انتخاب دستگاه». The interceptor
  // announces it once per state of the user; a changed state lets the next refusal be announced.
  const completionCheckInFlightRef = useRef(false)
  const accountGateState = `${currentUser?.id || 0}:${Boolean(currentUser?.accountCompletion)}:${Boolean(
    currentUser?.deviceLimit
  )}`
  useEffect(() => {
    authHelper.resetAccountGateNotice()
  }, [accountGateState])
  useEffect(() => {
    const handleIncomplete = async () => {
      if (completionCheckInFlightRef.current) return
      completionCheckInFlightRef.current = true
      try {
        const response = await getUserByToken()
        if (response?.data) {
          setAuth(response.data)
          authHelper.setAuth(response.data)
          setCurrentUser(response.data)
        }
      } catch {
        // A 401 here signs out through the interceptor; anything else leaves the page as it is.
      } finally {
        completionCheckInFlightRef.current = false
      }
    }
    window.addEventListener(ACCOUNT_INCOMPLETE_EVENT, handleIncomplete)
    return () => window.removeEventListener(ACCOUNT_INCOMPLETE_EVENT, handleIncomplete)
  }, [])

  useEffect(() => {
    if (!currentUser) {
      return
    }

    lastSessionValidatedAtRef.current = Date.now()

    const refreshCurrentUser = async (force = false) => {
      if (isSessionValidationInFlightRef.current) {
        return
      }

      if (document.visibilityState === 'hidden') {
        return
      }

      const now = Date.now()
      if (!force && now - lastSessionValidatedAtRef.current < SESSION_REVALIDATE_AFTER_MS) {
        return
      }

      isSessionValidationInFlightRef.current = true
      try {
        const response = await getUserByToken()
        lastSessionValidatedAtRef.current = Date.now()
        rememberTabBusiness(response.data)
        // The tab's business was taken from it (the user taken off it, or «محدودیت ورود»): what the page holds belongs
        // to it, so the page starts again in the business the server gave.
        if (serverKeepsBusinessPerTab() && response.data?.cpi !== currentUser?.cpi) {
          setBusinessNotice('کسب‌وکاری که در این تب باز بود دیگر در دسترس شما نیست؛ کسب‌وکار دیگری باز شد.')
          window.location.reload()
          return
        }
        setAuth(response.data)
        authHelper.setAuth(response.data)
        setCurrentUser(response.data)
      } catch (error: any) {
        if (error?.response?.status === 401) {
          clearLocalAuth()
        }
      } finally {
        isSessionValidationInFlightRef.current = false
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshCurrentUser()
      }
    }
    const handleFocus = () => refreshCurrentUser()
    const handleOnline = () => refreshCurrentUser(true)
    // Working in the page renews the session too (at most every five minutes). The bank's install issues its cookie
    // for an hour (12 minutes until 2026-10-26), and someone working in one tab without leaving it was signed out
    // mid-work (an administrator changing the invoice list's season, 2026-10-25); being away longer still signs out.
    const handleActivity = () => refreshCurrentUser()
    const activity = {capture: true, passive: true}

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)
    window.addEventListener('online', handleOnline)
    document.addEventListener('pointerdown', handleActivity, activity)
    document.addEventListener('keydown', handleActivity, activity)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('online', handleOnline)
      document.removeEventListener('pointerdown', handleActivity, activity)
      document.removeEventListener('keydown', handleActivity, activity)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, currentUser?.cpi])

  return (
    <AuthContext.Provider value={{auth, saveAuth, currentUser, setCurrentUser, logout}}>
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {auth, saveAuth, setCurrentUser} = useAuth()
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(true)

  useEffect(() => {
    let active = true
    const requestUser = async () => {
      try {
        if (!didRequest.current) {
          const usr = await getUserByToken(auth?.tk)
          if (usr && usr.data) {
            rememberTabBusiness(usr.data)
            saveAuth(usr.data)
            if (active) {
              setCurrentUser(usr.data)
            }
          }
        }
      } catch (error: any) {
        if (error?.response?.status !== 401) {
          console.error(error)
        }
        if (active && error?.response?.status === 401) {
          saveAuth(undefined)
        }
      } finally {
        if (active) {
          setShowSplashScreen(false)
        }
      }
    }

    requestUser()
    return () => {
      active = false
      didRequest.current = true
    }
    // eslint-disable-next-line
  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthProvider, AuthInit, useAuth}
